/**
 * Pogrubienie, pochylenie i podkreślenie napisu zapisane tak, żeby działało też u innych zmarłych.
 *
 * Opis stylów (zapisywany w zmianach układu jako `textStyles`):
 * {
 *   whole: { fontWeight: 'bold', fontStyle: 'normal', underline: false },   // styl całego napisu
 *   fragments: [
 *     { field: 'f@58:formatMassDate', fontWeight: 'bold' },                  // całe pole - dowolnej długości
 *     { literal: 'l@0', start: 0, end: 8, fontStyle: 'italic' },             // fragment stałego tekstu
 *     { start: 10, end: 20, underline: true },                               // tekst przepisany ręcznie
 *   ],
 *   styledText: '…',   // tylko przy fragmentach po literach - nakładamy je, gdy tekst jest identyczny
 * }
 *
 * Fragmenty pól i stałego tekstu wskazujemy przez mapę tekstu (textDSL/segments.js).
 */

export const STYLE_PROPS = ['fontWeight', 'fontStyle', 'underline'];

const normalizers = {
    fontWeight: value => (value === 'bold' || Number(value) >= 600 ? 'bold' : 'normal'),
    fontStyle: value => (value === 'italic' || value === 'oblique' ? 'italic' : 'normal'),
    underline: value => Boolean(value),
};

const isMapValid = (textbox, textMap) => Boolean(textMap) && textMap.text === (textbox.text || '');

/** Aktualne style napisu jako opis niezależny od pozycji liter (gdy to możliwe) */
export function describeTextStyles(textbox, textMap) {
    const text = textbox.text || '';
    const completeStyles = text.length > 0 ? textbox.getSelectionStyles(0, text.length, true) : [];
    const letterIndices = [];
    for (let i = 0; i < text.length; i += 1) {
        if (text[i] !== '\n') letterIndices.push(i);
    }
    const mapValid = isMapValid(textbox, textMap);
    const description = { whole: {}, fragments: [] };

    STYLE_PROPS.forEach((prop) => {
        const normalize = normalizers[prop];
        const values = completeStyles.map(style => normalize(style?.[prop]));
        const objectValue = normalize(textbox[prop]);

        if (letterIndices.length === 0) {
            description.whole[prop] = objectValue;
            return;
        }
        // Wszystkie litery tak samo → styl całego napisu (np. „B” bez zaznaczenia)
        const firstValue = values[letterIndices[0]];
        if (letterIndices.every(i => values[i] === firstValue)) {
            description.whole[prop] = firstValue;
            return;
        }

        description.whole[prop] = objectValue;
        const differing = letterIndices.filter(i => values[i] !== objectValue);

        if (mapValid) {
            const fields = new Map();
            const literalRuns = [];
            differing.forEach((i) => {
                const owner = textMap.owners[i];
                const segment = textMap.segments[owner.segment];
                if (segment.kind === 'field') {
                    // Część pola = całe pole (zaznaczenie i tak rozszerza się do całego pola)
                    if (!fields.has(segment.key)) fields.set(segment.key, values[i]);
                    return;
                }
                const last = literalRuns[literalRuns.length - 1];
                const continuesRun = last && last.literal === segment.key && last[prop] === values[i];
                // Powielone spacje (odstępy między słowami) mają tę samą pozycję co poprzednia litera
                if (continuesRun && owner.offset < last.end) return;
                if (continuesRun && owner.offset === last.end) {
                    last.end += 1;
                    return;
                }
                literalRuns.push({ literal: segment.key, start: owner.offset, end: owner.offset + 1, [prop]: values[i] });
            });
            fields.forEach((value, key) => description.fragments.push({ field: key, [prop]: value }));
            literalRuns.forEach(run => description.fragments.push(run));
            return;
        }

        // Bez mapy (tekst przepisany ręcznie, napis dodany w edytorze) - fragmenty po literach
        let run = null;
        differing.forEach((i) => {
            if (run && run.end === i && run[prop] === values[i]) {
                run.end += 1;
                return;
            }
            run = { start: i, end: i + 1, [prop]: values[i] };
            description.fragments.push(run);
        });
        description.styledText = text;
    });

    return description;
}

/** Nakłada opis stylów na napis; fragmentów, których nie da się wskazać w obecnym tekście, nie nakłada */
export function applyTextStyles(textbox, description, textMap) {
    if (!description || typeof textbox.setSelectionStyles !== 'function') return;

    STYLE_PROPS.forEach((prop) => {
        const value = description.whole?.[prop];
        if (value === undefined) return;
        textbox.set(prop, value);
        textbox.removeStyle(prop);
    });

    const text = textbox.text || '';
    const mapValid = isMapValid(textbox, textMap);

    (description.fragments || []).forEach((fragment) => {
        const prop = STYLE_PROPS.find(key => fragment[key] !== undefined);
        if (!prop) return;

        let indices = [];
        if (fragment.field || fragment.literal) {
            if (!mapValid) return;
            textMap.owners.forEach((owner, i) => {
                const key = textMap.segments[owner.segment].key;
                if (fragment.field && key === fragment.field) indices.push(i);
                if (fragment.literal && key === fragment.literal && owner.offset >= fragment.start && owner.offset < fragment.end) indices.push(i);
            });
        } else {
            if (description.styledText !== text) return;
            for (let i = fragment.start; i < Math.min(fragment.end, text.length); i += 1) indices.push(i);
        }

        indices.forEach(i => textbox.setSelectionStyles({ [prop]: fragment[prop] }, i, i + 1));
    });

    if (typeof textbox.initDimensions === 'function') textbox.initDimensions();
    if (typeof textbox.setCoords === 'function') textbox.setCoords();
}
